import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import PropTypes from "prop-types";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import PageHeader from "../PageHeader";
import useAgents from "../../hooks/useAgents";
import { investingApi } from "../../services/api";
import { apiErrorMessage, parseApiError } from "../../services/apiError";
import { displayName } from "../tasks/userDisplay";
import { formatDay, whoName } from "./lib/format";
import { revealLater } from "./lib/reveal";
import { myAgents } from "./lib/researchTask";
import {
  CAP_FIELDS,
  SETTING_LABELS,
  executorName,
  executorProblem,
  formErrors,
  formFromSettings,
  patchFromForm,
  settingLabel,
  settingValue,
} from "./lib/settingsForm";

const MAIN = "h-11 md:h-9";
const REASON_MAX = 1000; // backend/investing/constants.py
const HISTORY_LIMIT = 50; // constants.py SETTINGS_HISTORY_LIMIT
// When each lockable setting locks, and why once it has
// (Settings.locked_fields in backend/investing/models.py).
const LOCKS = {
  benchmark_symbol: {
    until:
      "Locks at the account's first contribution, distribution or snapshot — each records the benchmark's price.",
    locked: "Locked: the account's contributions, distributions and snapshots record its prices.",
  },
  inception: {
    until: "Locks at the account's first snapshot, when the scoreboard starts.",
    locked: "Locked: the scoreboard is measured from the account's first snapshot.",
  },
};

// The fields' keys in the order the form reads, for focusing the first
// invalid one and for knowing which server errors land on a field.
const FIELD_ORDER = [
  ...CAP_FIELDS.map((f) => `caps.${f.key}`),
  "drift_band_pct",
  "min_trade_usd",
  "executor",
  "tax_rate_short",
  "tax_rate_long",
  "benchmark_symbol",
  "inception",
  "reason",
];

/** One setting: its label, input, unit, what it changes, and any error. */
function SettingField({ id, label, unit, help, error, inputRef, ...input }) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex items-center gap-2">
        <Input
          ref={inputRef}
          id={id}
          className={cn(MAIN, unit ? "max-w-32" : "max-w-48")}
          aria-invalid={error ? true : undefined}
          aria-describedby={`${id}-help${error ? ` ${id}-error` : ""}`}
          {...input}
        />
        {unit && <span className="text-sm text-muted-foreground">{unit}</span>}
      </div>
      <p id={`${id}-help`} className="m-0 text-xs text-muted-foreground">
        {help}
      </p>
      {error && (
        <p id={`${id}-error`} className="m-0 text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

SettingField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  unit: PropTypes.string,
  help: PropTypes.string.isRequired,
  error: PropTypes.string,
  inputRef: PropTypes.func,
};

function Section({ title, intro, error, children }) {
  return (
    <fieldset className="m-0 min-w-0 rounded-lg border bg-card p-4">
      <legend className="float-left mb-1 w-full p-0 text-lg font-semibold">{title}</legend>
      <p className="clear-left mt-0 mb-3 text-sm text-muted-foreground">{intro}</p>
      {error && <p className="mt-0 mb-3 text-sm text-destructive">{error}</p>}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>
    </fieldset>
  );
}

Section.propTypes = {
  title: PropTypes.string.isRequired,
  intro: PropTypes.string.isRequired,
  error: PropTypes.string,
  children: PropTypes.node.isRequired,
};

/** The newest changes, each with what, from and to, why, who and when. */
function ChangeHistory({ changes }) {
  const count = changes.length;
  let lead;
  if (count === 0) lead = "No changes yet — each one you save is kept here with its reason.";
  else if (count >= HISTORY_LIMIT) lead = `The newest ${HISTORY_LIMIT} changes, newest first; older ones are kept in the record.`;
  else lead = `${count} ${count === 1 ? "change" : "changes"}, newest first.`;
  return (
    <section aria-labelledby="settings-history" className="rounded-lg border bg-card p-4">
      <h2 id="settings-history" className="mt-0 mb-1 text-lg font-semibold">
        Change history
      </h2>
      <p className="mt-0 mb-3 text-sm text-muted-foreground">{lead}</p>
      {count > 0 && (
        <ol className="m-0 flex list-none flex-col gap-2 p-0">
          {changes.map((change) => (
            <li key={change.id} className="rounded-md border border-border px-3 py-2 text-sm">
              <p className="m-0 font-medium wrap-anywhere">
                {settingLabel(change.field)}: {settingValue(change.field, change.before)} →{" "}
                {settingValue(change.field, change.after)}
              </p>
              <p className="m-0 wrap-anywhere">{change.reason}</p>
              <p className="m-0 text-muted-foreground">
                {whoName(change.by)}, {formatDay(change.at)}
              </p>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

ChangeHistory.propTypes = {
  changes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

/** The form, for a person: it loads the settings and the person's agents. */
function SettingsForm({ me }) {
  const id = useId();
  const { agents, loading: agentsLoading, error: agentsError, refresh: refreshAgents } = useAgents();
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const refs = useRef({});
  const formErrorRef = useRef(null);
  const fieldRef = useCallback(
    (key) => (node) => {
      refs.current[key] = node;
    },
    [],
  );
  // One read at a time: Retry is offered only once a read has failed.
  useEffect(() => {
    setLoadError(null);
    investingApi.settings.get().then(
      ({ data }) => {
        setCurrent(data);
        setForm(formFromSettings(data));
      },
      (err) => setLoadError(err),
    );
  }, [reloadTick]);

  // The first field in error; the caps' shared rule is the first cap's.
  const firstInError = (found) =>
    FIELD_ORDER.find((k) => found[k]) ?? (found.caps ? `caps.${CAP_FIELDS[0].key}` : null);
  const focusFirst = (found) => {
    const key = firstInError(found);
    if (key) refs.current[key]?.focus();
  };

  const setField = (key, value) => {
    setForm((prev) => {
      if (key.startsWith("caps.")) return { ...prev, caps: { ...prev.caps, [key.slice(5)]: value } };
      return { ...prev, [key]: value };
    });
    // Editing a cap answers its own error and the caps' shared rule.
    const answered = key.startsWith("caps.") ? [key, "caps"] : [key];
    setErrors((prev) => {
      if (!answered.some((k) => prev[k])) return prev;
      const next = { ...prev };
      answered.forEach((k) => delete next[k]);
      return next;
    });
  };

  const submit = async (event) => {
    event.preventDefault();
    // No busy check: the submit button is disabled while it saves.
    setStatus("");
    setFormError(null);
    const found = formErrors(form);
    const why = reason.trim();
    if (!why) found.reason = "Say why — it's kept with each change in the history below.";
    if (Object.keys(found).length) {
      setErrors(found);
      focusFirst(found);
      return;
    }
    const { body, changed } = patchFromForm(form, current);
    if (changed.length === 0) {
      setErrors({});
      setStatus("Nothing has changed — edit a setting first.");
      return;
    }
    setErrors({});
    setSaving(true);
    try {
      const { data } = await investingApi.settings.patch({ ...body, reason: why });
      setSaving(false);
      setCurrent(data);
      setForm(formFromSettings(data));
      setReason("");
      setStatus(
        `Saved ${changed.length} ${changed.length === 1 ? "change" : "changes"}: ${changed.join(", ")}. The history below keeps your reason.`,
      );
    } catch (err) {
      setSaving(false);
      const parsed = parseApiError(err, "Couldn't save the settings.");
      const onFields = {};
      const elsewhere = [];
      for (const [key, value] of Object.entries(parsed.fieldErrors)) {
        const text = [].concat(value).join(" ");
        if (FIELD_ORDER.includes(key) || key === "caps") onFields[key] = text;
        else elsewhere.push(`${key}: ${text}`);
      }
      setErrors(onFields);
      setFormError([parsed.formError, ...elsewhere].filter(Boolean).join(" — ") || null);
      // Once the fields are enabled again (they're disabled while saving):
      // the first field in error, else the message.
      if (firstInError(onFields)) setTimeout(() => focusFirst(onFields), 0);
      else revealLater(formErrorRef);
    }
  };

  if (!current) {
    return loadError ? (
      <Alert variant="destructive" role="alert">
        <AlertDescription className="flex flex-wrap items-center gap-2">
          <span>{apiErrorMessage(loadError, "Couldn't load the settings.")}</span>{" "}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-11 md:h-8"
            onClick={() => setReloadTick((tick) => tick + 1)}
          >
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    ) : (
      <div className="py-12 text-center">
        <Loader2 className="inline size-6 animate-spin text-muted-foreground" aria-hidden="true" />
        <span className="sr-only">Loading the settings…</span>
      </div>
    );
  }

  const editable = Boolean(current.can_edit);
  const locked = new Set(current.locked ?? []);
  const disabled = (key) => !editable || saving || locked.has(key);
  const problem = executorProblem(current, agentsLoading ? [] : agents, me);
  const mine = myAgents(agents, me);
  const executorOptions = mine.map((agent) => ({ value: String(agent.id), label: displayName(agent) }));
  if (current.executor && !executorOptions.some((o) => o.value === String(current.executor))) {
    const name = executorName(current);
    let label = name;
    if (!current.executor_valid) label = `${name} — can no longer act`;
    else if (current.executor === me?.id) label = `${name} (you)`;
    executorOptions.push({ value: String(current.executor), label });
  }

  const field = (key, props) => (
    <SettingField
      key={key}
      id={`${id}-${key.replace(".", "-")}`}
      inputRef={fieldRef(key)}
      error={errors[key]}
      disabled={disabled(key.startsWith("caps.") ? "caps" : key)}
      onChange={(event) => setField(key, event.target.value)}
      {...props}
    />
  );
  // What it is, then whether it can still change.
  const lockHelp = (key, help) => `${help} ${locked.has(key) ? LOCKS[key].locked : LOCKS[key].until}`;

  return (
    <>
      {!current.saved && (
        <p className="mt-0 mb-4 rounded-md border border-border bg-muted/40 p-3 text-sm">
          These are the defaults — nothing is saved until your first change.
        </p>
      )}
      {!editable && (
        <p className="mt-0 mb-4 rounded-md border border-border bg-muted/40 p-3 text-sm">
          This session can&apos;t change the settings: only you can, signed in to Mono in your own browser. Every API
          token and connector is refused.
        </p>
      )}

      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        <Section
          title="Caps on a target"
          intro="Checked on a target's weights when a change is proposed. A breach doesn't block it: it needs an override reason, which you see when you decide. Percentages are of the whole account."
          error={errors.caps}
        >
          {CAP_FIELDS.map((cap) =>
            field(`caps.${cap.key}`, {
              label: cap.label,
              unit: cap.unit,
              help: cap.help,
              inputMode: cap.kind === "int" ? "numeric" : "decimal",
              value: form.caps[cap.key],
            }),
          )}
        </Section>

        <Section
          title="Syncing the account"
          intro="How a sync turns the approved target into orders, and who carries it out. Robinhood still asks you to approve every order on your phone."
        >
          {field("drift_band_pct", {
            label: SETTING_LABELS.drift_band_pct,
            unit: "points",
            help: "A sync trades only the holdings further than this from their target weight, either way, so prices moving don't churn the account. Default 2 percentage points.",
            inputMode: "decimal",
            value: form.drift_band_pct,
          })}
          {field("min_trade_usd", {
            label: SETTING_LABELS.min_trade_usd,
            unit: "dollars",
            help: "Orders smaller than this are dropped from a sync plan. Default $25.",
            inputMode: "decimal",
            value: form.min_trade_usd,
          })}
          <div className="flex min-w-0 flex-col gap-1.5 sm:col-span-2">
            <Label htmlFor={`${id}-executor`}>{SETTING_LABELS.executor}</Label>
            {/* Bounded by a wrapper: NativeSelect's chevron sits at its wrapper's edge. */}
            <div className="max-w-sm">
              <NativeSelect
                ref={fieldRef("executor")}
                id={`${id}-executor`}
                value={form.executor}
                className={MAIN}
                disabled={disabled("executor")}
                aria-invalid={errors.executor ? true : undefined}
                aria-describedby={`${id}-executor-help${problem ? ` ${id}-executor-problem` : ""}${
                  errors.executor ? ` ${id}-executor-error` : ""
                }`}
                onChange={(event) => setField("executor", event.target.value)}
              >
                <option value="">Nobody</option>
                {executorOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <p id={`${id}-executor-help`} className="m-0 text-xs text-muted-foreground">
              The one agent that records the account&apos;s snapshots, makes sync plans and records fills — no one
              else can. You can name only your own active agents.
              {agentsLoading ? " Loading your agents…" : ""}
            </p>
            {problem && (
              <p id={`${id}-executor-problem`} className="m-0 rounded-md border border-warning bg-warning/10 p-2 text-sm">
                {problem}
              </p>
            )}
            {agentsError && (
              <p className="m-0 flex flex-wrap items-center gap-2 text-sm">
                Couldn&apos;t list your agents ({apiErrorMessage(agentsError, "error")}).{" "}
                <Button type="button" variant="outline" size="sm" className="h-11 md:h-8" onClick={() => refreshAgents()}>
                  Retry
                </Button>
              </p>
            )}
            {!agentsLoading && !agentsError && mine.length === 0 && (
              <p className="m-0 text-sm">
                You have no active agents to name. Mint one under <Link to="/agents">Agents</Link>.
              </p>
            )}
            {errors.executor && (
              <p id={`${id}-executor-error`} className="m-0 text-sm text-destructive">
                {errors.executor}
              </p>
            )}
          </div>
        </Section>

        <Section
          title="Tax and the benchmark"
          intro="What the scoreboard measures the fund against, from when, and the tax it estimates on this year's net realized gains."
        >
          {field("tax_rate_short", {
            label: SETTING_LABELS.tax_rate_short,
            unit: "%",
            help: "Applied to this year's net short-term gains for the scoreboard's after-tax figure. Default 32%.",
            inputMode: "decimal",
            value: form.tax_rate_short,
          })}
          {field("tax_rate_long", {
            label: SETTING_LABELS.tax_rate_long,
            unit: "%",
            help: "Applied to this year's net long-term gains for the scoreboard's after-tax figure. Default 15%.",
            inputMode: "decimal",
            value: form.tax_rate_long,
          })}
          {field("benchmark_symbol", {
            label: SETTING_LABELS.benchmark_symbol,
            help: lockHelp(
              "benchmark_symbol",
              "The fund is measured against the same money held in this — a ticker like VTI.",
            ),
            autoCapitalize: "characters",
            maxLength: 10,
            value: form.benchmark_symbol,
          })}
          {field("inception", {
            label: SETTING_LABELS.inception,
            type: "date",
            help: lockHelp("inception", "The day the scoreboard counts from; the first deposit sets it."),
            value: form.inception,
          })}
        </Section>

        {editable && (
          <div className="flex flex-col gap-3 rounded-lg border bg-card p-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${id}-reason`}>Reason</Label>
              <Textarea
                ref={fieldRef("reason")}
                id={`${id}-reason`}
                value={reason}
                maxLength={REASON_MAX}
                placeholder="e.g. Room for a conviction-5 holding"
                className="max-h-48 min-h-20"
                disabled={saving}
                aria-invalid={errors.reason ? true : undefined}
                aria-describedby={`${id}-reason-help${errors.reason ? ` ${id}-reason-error` : ""}`}
                onChange={(event) => {
                  setReason(event.target.value);
                  setErrors((prev) => {
                    if (!prev.reason) return prev;
                    const next = { ...prev };
                    delete next.reason;
                    return next;
                  });
                }}
              />
              <p id={`${id}-reason-help`} className="m-0 text-xs text-muted-foreground">
                Required. Kept with each setting you change, in the history below.
              </p>
              {errors.reason && (
                <p id={`${id}-reason-error`} className="m-0 text-sm text-destructive">
                  {errors.reason}
                </p>
              )}
            </div>
            {formError && (
              <Alert ref={formErrorRef} variant="destructive" role="alert" tabIndex={-1} className="outline-none">
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}
            <div className="flex flex-wrap items-center gap-3">
              <Button type="submit" className={MAIN} disabled={saving}>
                {saving && <Loader2 className="animate-spin" aria-hidden="true" />}
                {saving ? "Saving…" : "Save changes"}
              </Button>
              <p role="status" className="m-0 text-sm">
                {status}
              </p>
            </div>
          </div>
        )}
      </form>

      <div className="mt-4">
        <ChangeHistory changes={Array.isArray(current.changes) ? current.changes : []} />
      </div>
    </>
  );
}

SettingsForm.propTypes = {
  me: PropTypes.shape({ id: PropTypes.number, username: PropTypes.string }).isRequired,
};

/**
 * Settings (spec §5.6, D5, D12, D13): the caps a target is checked
 * against, the drift band and minimum trade a sync uses, the executor, the
 * tax rates, and the benchmark and inception (shown locked once the API
 * says so). Each setting says what it changes; a reason is required to
 * save, and only what changed is sent. The change history is the newest 50.
 *
 * A person's only (spec §2.1): nothing loads until `me` says human — not
 * while it's unknown, never for an agent, and not when /users/me/ failed
 * (that says so, with a Retry). The API's `can_edit` is the server's own
 * answer; when it's false the page is read-only and says why, and a 403 is
 * still shown as the server says it.
 */
function InvestingSettings() {
  const { me, meLoading, meError, refreshMe } = useOutletContext() || {};
  const isPerson = me?.kind === "human";
  let body;
  if (isPerson) body = <SettingsForm me={me} />;
  else if (!meLoading && !me) {
    body = (
      <Alert variant="destructive" role="alert">
        <AlertDescription className="flex flex-wrap items-center gap-2">
          <span>
            Couldn&apos;t check who&apos;s signed in
            {meError ? ` (${apiErrorMessage(meError, "error")})` : ""}, so the settings stay hidden — they&apos;re a
            person&apos;s only.
          </span>{" "}
          {refreshMe && (
            <Button type="button" variant="outline" size="sm" className="h-11 md:h-8" onClick={() => refreshMe()}>
              Retry
            </Button>
          )}
        </AlertDescription>
      </Alert>
    );
  } else if (!meLoading && me?.kind === "agent") {
    body = (
      <div className="rounded-lg border bg-card p-4">
        <p className="m-0">
          Settings are a person&apos;s: only the person, signed in to Mono in their own browser, sees and changes
          the caps, the executor and the tax rates. An agent works from the board.
        </p>
      </div>
    );
  } else {
    body = <p className="m-0 text-sm text-muted-foreground">Checking who&apos;s signed in…</p>;
  }
  return (
    <div className="page-container mt-6 pt-4">
      <div className="max-w-3xl">
        <PageHeader
          title="Settings"
          subtitle={`How the fund is run: the caps a target is checked against, how a sync trades and who carries it out, and the tax rates the scoreboard uses. ${
            isPerson ? "Only you change these" : "Only the person changes these"
          }; every change is kept with its reason.`}
        />
        {body}
      </div>
    </div>
  );
}

export default InvestingSettings;
